<nz-card>
  <h2>{{ 'TEAM_ACCESS.TITLE' | translate }}</h2>

  <p *ngIf="seatInfo">
    {{ 'TEAM_ACCESS.USERS_USED_MAX' | translate }}:
    {{ seatInfo.seatsUsed }} / {{ seatInfo.effectiveSeatLimit ?? '∞' }}
  </p>

  <div class="seat-actions" *ngIf="canManageSeatLimit">
    <button nz-button nzType="default" type="button" (click)="openIncreaseSeatLimitModal()">
      {{ 'TEAM_ACCESS.INCREASE_LIMIT.BUTTON' | translate }}
    </button>
  </div>

  <div class="invite-row" *ngIf="canInvite()">
    <input nz-input [(ngModel)]="inviteDisplayName" [placeholder]="'TEAM_ACCESS.INVITE_DISPLAY_NAME_LABEL' | translate" />
    <input nz-input [(ngModel)]="inviteEmail" [placeholder]="'TEAM_ACCESS.EMAIL' | translate" />
    <nz-select [(ngModel)]="inviteCompanyRole" (ngModelChange)="selectedRoleInfo = $event">
      <nz-option
        *ngFor="let role of roleOptions"
        [nzValue]="role"
        [nzLabel]="roleLabelKey(role) | translate"
      ></nz-option>
    </nz-select>
    <button nz-button nzType="primary" (click)="createInvite()" [disabled]="noSeatsAvailable()">
      {{ 'TEAM_ACCESS.INVITE_USER' | translate }}
    </button>
  </div>
  <p *ngIf="canInvite() && noSeatsAvailable() && !canManageSeatLimit">
    {{ 'ERRORS.SEAT_LIMIT_REACHED_CONTACT' | translate }}
  </p>

  <nz-card nzType="inner" *ngIf="canInvite()" class="role-info-card">
    <h3>{{ 'TEAM_ACCESS.ROLE_INFO_TITLE' | translate }}</h3>
    <p>
      <strong>{{ roleLabelKey(selectedRoleInfo) | translate }}</strong>
      -
      {{ roleDescription(selectedRoleInfo) }}
    </p>
    <div>
      <strong>{{ 'TEAM_ACCESS.CAN_DO' | translate }}</strong>
      <ul>
        <li *ngFor="let item of getRoleCanLabels(selectedRoleInfo)">{{ item }}</li>
      </ul>
    </div>
    <div *ngIf="getRoleCannotLabels(selectedRoleInfo).length">
      <strong>{{ 'TEAM_ACCESS.CANNOT_DO' | translate }}</strong>
      <ul>
        <li *ngFor="let item of getRoleCannotLabels(selectedRoleInfo)">{{ item }}</li>
      </ul>
    </div>
  </nz-card>

  <nz-table [nzData]="users" [nzLoading]="loading" [nzPageSize]="10">
    <thead>
    <tr>
      <th>{{ 'TEAM_ACCESS.DISPLAY_NAME' | translate }}</th>
      <th>{{ 'TEAM_ACCESS.EMAIL' | translate }}</th>
      <th>{{ 'TEAM_ACCESS.ROLE' | translate }}</th>
      <th>{{ 'TEAM_ACCESS.STATUS' | translate }}</th>
      <th nzWidth="300px">{{ 'TEAM_ACCESS.ACTIONS' | translate }}</th>
    </tr>
    </thead>
    <tbody>
    <tr *ngFor="let user of users">
      <td>{{ user.displayName || '-' }}</td>
      <td>{{ user.email }}</td>
      <td>
        <nz-select
          [ngModel]="user.companyRole"
          (ngModelChange)="selectedRoleInfo = $event; updateRole(user, $event)"
          [nzDisabled]="!canUpdate()"
        >
          <nz-option
            *ngFor="let role of roleOptions"
            [nzValue]="role"
            [nzLabel]="roleLabelKey(role) | translate"
          ></nz-option>
        </nz-select>
      </td>
      <td>
        {{
          !user.isDeactivated
            ? ('TEAM_ACCESS.STATUS_ACTIVE' | translate)
            : (user.deactivationType === 'admin'
              ? ('TEAM_ACCESS.STATUS_DEACTIVATED_ADMIN' | translate)
              : ('TEAM_ACCESS.STATUS_DEACTIVATED_SELF' | translate))
        }}
      </td>
      <td class="actions-cell">
        <div class="actions">
          <button nz-button nzSize="small" (click)="openEditUserModal(user)" [disabled]="!canUpdate()">
            {{ 'TEAM_ACCESS.EDIT_DISPLAY_NAME' | translate }}
          </button>
          <button nz-button nzSize="small" (click)="toggleDeactivation(user)" [disabled]="!canDeactivate()">
            {{ user.isDeactivated ? ('TEAM_ACCESS.REACTIVATE' | translate) : ('TEAM_ACCESS.DEACTIVATE' | translate) }}
          </button>
          <button nz-button nzDanger nzSize="small" (click)="removeUser(user)" [disabled]="!canRemove()">
            {{ 'TEAM_ACCESS.REMOVE' | translate }}
          </button>
        </div>
      </td>
    </tr>
    </tbody>
  </nz-table>

  <nz-modal
    [(nzVisible)]="editUserModalVisible"
    [nzTitle]="'TEAM_ACCESS.EDIT_DISPLAY_NAME' | translate"
    (nzOnCancel)="closeEditUserModal()"
    (nzOnOk)="saveUserEdits()"
    [nzOkText]="'COMMON.SAVE' | translate"
    [nzCancelText]="'COMMON.CANCEL' | translate"
  >
    <ng-container *nzModalContent>
      <label>{{ 'TEAM_ACCESS.DISPLAY_NAME' | translate }}</label>
      <input nz-input [(ngModel)]="editDisplayName" style="margin-bottom: 12px;" />
      <label>{{ 'TEAM_ACCESS.ROLE' | translate }}</label>
      <nz-select [(ngModel)]="editCompanyRole" style="width: 100%;">
        <nz-option
          *ngFor="let role of roleOptions"
          [nzValue]="role"
          [nzLabel]="roleLabelKey(role) | translate"
        ></nz-option>
      </nz-select>
    </ng-container>
  </nz-modal>

  <nz-modal
    [(nzVisible)]="increaseSeatLimitModalVisible"
    [nzTitle]="'TEAM_ACCESS.INCREASE_LIMIT.TITLE' | translate"
    [nzOkText]="'TEAM_ACCESS.INCREASE_LIMIT.CONFIRM' | translate"
    [nzCancelText]="'COMMON.CANCEL' | translate"
    [nzOkLoading]="increaseSeatLimitSubmitting"
    [nzOkDisabled]="!increaseSeatLimitForm || increaseSeatLimitForm.invalid || !!getSeatLimitIncreaseErrorMessage()"
    (nzOnCancel)="closeIncreaseSeatLimitModal()"
    (nzOnOk)="submitIncreaseSeatLimit()"
  >
    <ng-container *nzModalContent>
      <form nz-form nzLayout="vertical" [formGroup]="increaseSeatLimitForm">
        <div class="seat-limit-modal__summary">
          <div class="seat-limit-modal__card">
            <span>{{ 'TEAM_ACCESS.INCREASE_LIMIT.CURRENT_LIMIT' | translate }}</span>
            <strong>{{ effectiveSeatLimit ?? 0 }}</strong>
          </div>
          <div class="seat-limit-modal__card">
            <span>{{ 'TEAM_ACCESS.INCREASE_LIMIT.USED' | translate }}</span>
            <strong>{{ usedSeatsCount }}</strong>
          </div>
          <div class="seat-limit-modal__card">
            <span>{{ 'TEAM_ACCESS.INCREASE_LIMIT.REMAINING' | translate }}</span>
            <strong>{{ remainingSeatsCount ?? 0 }}</strong>
          </div>
          <div class="seat-limit-modal__card seat-limit-modal__card--price" *ngIf="pricePerAdditionalUserNetYear">
            <span class="seat-limit-modal__card-label">
              <span>{{ 'TEAM_ACCESS.INCREASE_LIMIT.USER_PRICE' | translate }}</span>
              <small>{{ 'TEAM_ACCESS.INCREASE_LIMIT.EUR_PER_YEAR' | translate }}</small>
            </span>
            <strong>{{ formatMoney(pricePerAdditionalUserNetYear) }}</strong>
          </div>
        </div>

        <nz-form-item>
          <nz-form-label>{{ 'TEAM_ACCESS.INCREASE_LIMIT.NEW_TOTAL_LABEL' | translate }}</nz-form-label>
          <nz-form-control [nzErrorTip]="getSeatLimitIncreaseErrorMessage()">
            <nz-input-number
              formControlName="newSeatLimit"
              [nzMin]="1"
              [nzStep]="1"
              [nzPrecision]="0"
              class="seat-limit-modal__input"
            ></nz-input-number>
          </nz-form-control>
        </nz-form-item>

        <label nz-checkbox formControlName="confirmedImmediateInvoice" class="seat-limit-modal__checkbox">
          {{ 'TEAM_ACCESS.INCREASE_LIMIT.CONFIRM_IMMEDIATE_INVOICE' | translate }}
        </label>
        <div class="seat-limit-modal__checkbox-error" *ngIf="getSeatLimitIncreaseConfirmationErrorMessage()">
          {{ getSeatLimitIncreaseConfirmationErrorMessage() }}
        </div>
      </form>
    </ng-container>
  </nz-modal>

  <h3>{{ 'TEAM_ACCESS.PENDING_INVITES' | translate }}</h3>
  <nz-table [nzData]="invites" [nzPageSize]="5">
    <thead>
    <tr>
      <th>{{ 'TEAM_ACCESS.EMAIL' | translate }}</th>
      <th>{{ 'TEAM_ACCESS.ROLE' | translate }}</th>
      <th>{{ 'TEAM_ACCESS.STATUS' | translate }}</th>
      <th>{{ 'TEAM_ACCESS.ACTIONS' | translate }}</th>
    </tr>
    </thead>
    <tbody>
    <tr *ngFor="let invite of invites">
      <td>{{ invite.email }}</td>
      <td>
        {{ roleLabelKey(invite.companyRole) | translate }}
      </td>
      <td>{{ inviteStatusKey(invite.status) | translate }}</td>
      <td>
        <button nz-button nzSize="small" (click)="revokeInvite(invite)" [disabled]="!canUpdate()">
          {{ 'TEAM_ACCESS.REVOKE' | translate }}
        </button>
      </td>
    </tr>
    </tbody>
  </nz-table>
</nz-card>
